import { ArrowRightIcon, FolderPlusIcon, GripVerticalIcon, PlusIcon } from 'lucide-react';
import { FC, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { cn } from '@/shared/lib';
import { IconButton } from '@/shared/ui';

type KmNodeActionsProps = {
  /** Группа принимает вложенные элементы, единица оценивания — нет */
  isGroup: boolean;
  /** Открыть карточку узла — кнопка нужна там, где по строке карточка не открывается */
  onOpenCard: () => void;
  onAddGroup: () => void;
  onAddAssessment: () => void;
  /** Ручка перетаскивания узла */
  handleRef: Ref<HTMLDivElement>;
  isDesktop?: boolean;
  /** Показывать действия только по наведению — на строках с раскрытием */
  revealOnHover?: string;
};

/**
 * Действия строки дерева структуры: переход к карточке, добавление вложенной группы
 * и единицы оценивания, ручка перетаскивания. Состав действий одинаков у листовых
 * и раскрываемых строк, поэтому живёт одним компонентом.
 */
export const KmNodeActions: FC<KmNodeActionsProps> = ({
  isGroup,
  onOpenCard,
  onAddGroup,
  onAddAssessment,
  handleRef,
  isDesktop,
  revealOnHover,
}) => {
  const { t } = useTranslation('expertReport');
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');

  const stopAnd = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    action();
  };

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      {/* Переход к карточке — когда контейнер не десктопный и клик по строке её не открывает */}
      {!isDesktop && (
        <IconButton
          label={t('openCard')}
          size="icon"
          className="size-6 shrink-0"
          icon={<ArrowRightIcon className="size-3.5" />}
          onClick={stopAnd(onOpenCard)}
        />
      )}

      {allowAdmin && (
        <div className={cn('flex items-center gap-0.5', revealOnHover)}>
          {isGroup && (
            <>
              <IconButton
                label={t('km.addGroup')}
                size="icon"
                className="size-6 shrink-0"
                icon={<FolderPlusIcon className="size-3.5" />}
                onClick={stopAnd(onAddGroup)}
              />
              <IconButton
                label={t('km.addAssessment')}
                size="icon"
                className="size-6 shrink-0"
                icon={<PlusIcon className="size-3.5" />}
                onClick={stopAnd(onAddAssessment)}
              />
            </>
          )}

          {/* Ручка перетаскивания: бокс size-6 — выравнивается с кнопками действий */}
          <div
            className="flex size-6 shrink-0 cursor-grab items-center justify-center text-muted-foreground"
            ref={handleRef}
            onClick={(event) => event.stopPropagation()}
          >
            <GripVerticalIcon size={14} />
          </div>
        </div>
      )}
    </div>
  );
};
